<script setup>
import { ref } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  onClose: { type: Function, default: () => {} },
});

const store = useStore();
const { t } = useI18n();

const name = ref('');
const content = ref('');
const category = ref('UTILITY');
const language = ref('pt_BR');
const showLoading = ref(false);

const rules = {
  name: { required, minLength: minLength(2) },
  content: { required },
};
const v$ = useVuelidate(rules, { name, content });

const resetForm = () => {
  name.value = '';
  content.value = '';
  category.value = 'UTILITY';
  v$.value.$reset();
};

const submit = async () => {
  const valid = await v$.value.$validate();
  if (!valid) return;
  showLoading.value = true;
  try {
    await store.dispatch('createMessageTemplate', {
      message_template: {
        name: name.value,
        content: content.value,
        category: category.value,
        language: language.value,
      },
    });
    useAlert(t('MESSAGE_TEMPLATES.ADD.API.SUCCESS_MESSAGE'));
    resetForm();
    props.onClose();
  } catch (error) {
    useAlert(error?.message || t('MESSAGE_TEMPLATES.ADD.API.ERROR_MESSAGE'));
  } finally {
    showLoading.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('MESSAGE_TEMPLATES.ADD.TITLE')"
      :header-content="$t('MESSAGE_TEMPLATES.ADD.DESC')"
    />
    <form class="flex flex-col w-full" @submit.prevent="submit">
      <div class="w-full">
        <label :class="{ error: v$.name.$error }">
          {{ $t('MESSAGE_TEMPLATES.FORM.NAME.LABEL') }}
          <input
            v-model="name"
            type="text"
            :placeholder="$t('MESSAGE_TEMPLATES.FORM.NAME.PLACEHOLDER')"
            @blur="v$.name.$touch"
          />
          <span v-if="v$.name.$error" class="message">
            {{ $t('MESSAGE_TEMPLATES.FORM.NAME.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label>{{ $t('MESSAGE_TEMPLATES.FORM.CATEGORY.LABEL') }}</label>
        <select v-model="category" class="mb-2">
          <option value="UTILITY">
            {{ $t('MESSAGE_TEMPLATES.CATEGORIES.UTILITY') }}
          </option>
          <option value="MARKETING">
            {{ $t('MESSAGE_TEMPLATES.CATEGORIES.MARKETING') }}
          </option>
          <option value="AUTHENTICATION">
            {{ $t('MESSAGE_TEMPLATES.CATEGORIES.AUTHENTICATION') }}
          </option>
        </select>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.content.$error }">
          {{ $t('MESSAGE_TEMPLATES.FORM.CONTENT.LABEL') }}
        </label>
        <textarea
          v-model="content"
          class="w-full min-h-[12.5rem] border rounded p-2 text-base"
          :class="{ 'border-red-500': v$.content.$error }"
          :placeholder="$t('MESSAGE_TEMPLATES.FORM.CONTENT.PLACEHOLDER')"
          @blur="v$.content.$touch"
        />
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="button"
          :label="$t('MESSAGE_TEMPLATES.CANCEL')"
          @click="onClose"
        />
        <NextButton
          type="submit"
          :label="$t('MESSAGE_TEMPLATES.ADD.SUBMIT')"
          :disabled="v$.name.$invalid || v$.content.$invalid || showLoading"
          :is-loading="showLoading"
        />
      </div>
    </form>
  </div>
</template>
